import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, MagnifyingGlass, Funnel, CurrencyInr, Wallet, ClockCounterClockwise, ChartBar } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export const LedgerSidePanel = ({ isOpen, onClose }) => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/transactions/all`);
      setTransactions(response.data);
      setFilteredTransactions(response.data);
    } catch (error) {
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTransactions();
    }
  }, [isOpen]);

  useEffect(() => {
    let filtered = transactions;

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(t => t.type === typeFilter);
    }

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(t => 
        t.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.amount.toString().includes(searchQuery)
      );
    }

    setFilteredTransactions(filtered);
  }, [searchQuery, typeFilter, transactions]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'sale':
        return <ChartBar size={18} weight="bold" className="text-[#0A7029]" />;
      case 'expense':
        return <CurrencyInr size={18} weight="bold" className="text-[#E53935]" />;
      case 'credit':
        return <Wallet size={18} weight="bold" className="text-[#002FA7]" />;
      case 'pending':
        return <ClockCounterClockwise size={18} weight="bold" className="text-[#D97706]" />;
      default:
        return null;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'sale':
        return 'text-[#0A7029]';
      case 'expense':
        return 'text-[#E53935]';
      case 'credit':
        return 'text-[#002FA7]';
      case 'pending':
        return 'text-[#D97706]';
      default:
        return 'text-[#111110]';
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="left" className="w-[400px] sm:w-[500px] rounded-none border-[#E5E5E0] p-0" data-testid="ledger-side-panel">
        <SheetHeader className="border-b border-[#E5E5E0] p-6">
          <div className="flex items-center justify-between">
            <SheetTitle className="heading text-xl font-bold text-[#111110]">All Ledger Entries</SheetTitle>
            <SheetClose asChild>
              <Button variant="ghost" size="icon" className="rounded-none" data-testid="close-ledger-panel">
                <X size={20} />
              </Button>
            </SheetClose>
          </div>
        </SheetHeader>

        {/* Filters */}
        <div className="p-4 space-y-3 border-b border-[#E5E5E0] bg-[#EFEFEA]">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666660]" />
            <Input
              placeholder="Search transactions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-none border-[#E5E5E0] bg-white"
              data-testid="ledger-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Funnel size={18} className="text-[#666660]" />
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="rounded-none border-[#E5E5E0] bg-white" data-testid="ledger-filter-select">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent className="rounded-none border-[#E5E5E0]">
                <SelectItem value="all">All Transactions</SelectItem>
                <SelectItem value="sale">Sales Only</SelectItem>
                <SelectItem value="expense">Expenses Only</SelectItem>
                <SelectItem value="credit">Credits Only</SelectItem>
                <SelectItem value="pending">Pending Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Transactions List */}
        <div className="overflow-y-auto h-[calc(100vh-220px)]" data-testid="ledger-transactions-list">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <span className="text-[#999990]">Loading...</span>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <span className="text-[#999990]">No transactions found</span>
            </div>
          ) : (
            <div className="divide-y divide-[#E5E5E0]">
              {filteredTransactions.map((transaction, index) => (
                <div 
                  key={transaction.id || `${transaction.type}-${transaction.date}-${index}`} 
                  className="p-4 hover:bg-[#EFEFEA]/50 transition-colors"
                  data-testid={`ledger-transaction-${index}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="mt-1">
                        {getTypeIcon(transaction.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold uppercase ${getTypeColor(transaction.type)}`}>
                            {transaction.type}
                          </span>
                          {transaction.credit_type && (
                            <span className="text-xs text-[#666660]">({transaction.credit_type})</span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-[#111110] truncate">{transaction.description}</p>
                        {transaction.remarks && (
                          <p className="text-xs text-[#666660] mt-1 truncate">{transaction.remarks}</p>
                        )}
                        <p className="text-xs date-text text-[#999990] mt-1">{transaction.date}</p>
                      </div>
                    </div>
                    <div className={`currency font-bold text-sm whitespace-nowrap ${getTypeColor(transaction.type)}`}>
                      {formatCurrency(transaction.amount)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary Footer */}
        {!loading && filteredTransactions.length > 0 && (
          <div className="border-t border-[#E5E5E0] p-4 bg-white">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-[#666660]">Total Entries</span>
              <span className="text-sm font-bold text-[#111110]">{filteredTransactions.length}</span>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default LedgerSidePanel;
